// Legacy order endpoints return localized database labels instead of enum codes.
// Normalize only at the read boundary; request payloads keep numeric status IDs.
const STATUS_ALIASES: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['New', ['جديدة', 'جديد']],
  ['CarParkedByEmployee', ['متوقفة', 'مركونة', 'تم ركن السيارة', 'Parked']],
  ['CarRequestedByCustomer', ['مطلوبة', 'Requested']],
  ['CarReadyByEmployee', ['جاهزة', 'Ready']],
  ['CarDeliveredByEmployee', ['مسلمة', 'تم التسليم', 'Delivered']],
  ['CancelCarRequestedByCustomer', ['طلب ملغى', 'Pickup request cancelled']]
];
const key = (value: unknown): string => typeof value === 'string'
  ? value.trim().replace(/\s/g, '').toLowerCase() : '';

export function orderStatusCode(value: unknown): string {
  const candidate = key(value);
  return STATUS_ALIASES.find(([code, aliases]) => [code, ...aliases].some(alias => key(alias) === candidate))?.[0]
    || 'UnknownStatus';
}

export function orderStatusClass(value: unknown): string {
  const classes: Record<string, string> = {
    New: 'new', CarParkedByEmployee: 'parked', CarRequestedByCustomer: 'request',
    CarReadyByEmployee: 'ready', CarDeliveredByEmployee: 'delivered',
    CancelCarRequestedByCustomer: 'cancelled'
  };
  return classes[orderStatusCode(value)] || 'unknown';
}

export function paymentStatusCode(value: unknown): string {
  if (['paid', 'مدفوع'].includes(key(value))) return 'Paid';
  if (['notpaid', 'unpaid', 'غيرمدفوع'].includes(key(value))) return 'NotPaid';
  return 'UnknownPaymentStatus';
}

// Keep direct delivery for open tickets, including cancelled pickup requests.
// Requesting a car is a customer action, not a staff status option.
export function canChangeOrderStatus(current: unknown, target: number, canDeliver = true): boolean {
  const transitions: Record<string, number[]> = {
    New: [2, 5], CarParkedByEmployee: [5], CarRequestedByCustomer: [4, 5],
    CarReadyByEmployee: [5], CancelCarRequestedByCustomer: [5]
  };
  const targetId = Number(target);
  return (canDeliver || targetId !== 5) && (transitions[orderStatusCode(current)] || []).includes(targetId);
}

export function orderProgressStep(status: unknown): number {
  return STATUS_ALIASES.slice(0, 5).findIndex(([code]) => code === orderStatusCode(status));
}

export function needsOrderCheckout(order: any): boolean {
  return Number(order?.PriceTypeId) === 2 && !order?.CheckoutDatetime && !isOrderPaid(order);
}

// Checkout recalculates elapsed-time pricing; a previous calculation is not final.
// Keep this independent of delivery permissions and the delivery prerequisite above.
export function canCheckoutOrder(order: any): boolean {
  return Number(order?.PriceTypeId) === 2 && isOrderUnpaid(order)
    && ['New', 'CarParkedByEmployee', 'CarRequestedByCustomer', 'CarReadyByEmployee',
      'CancelCarRequestedByCustomer'].includes(orderStatusCode(order?.Status));
}

export function orderAmount(order: any): number {
  return Number(order?.OrderCalculateResult?.Total ?? order?.TotalPrice ?? order?.Price);
}

export function isOrderPaid(order: any): boolean {
  return order?.IsPaid === true || paymentStatusCode(order?.PaymentStatus) === 'Paid';
}

export function isOrderUnpaid(order: any): boolean {
  return !isOrderPaid(order) && (order?.IsPaid === false || paymentStatusCode(order?.PaymentStatus) === 'NotPaid');
}

export class OrderActionError extends Error {
  constructor(public readonly code: string, public readonly order?: any) { super(code); }
}

export class OrderPaymentRequiredError extends OrderActionError {
  constructor(order: any) { super('orders.payment_info', order); }
}

export function orderActionMessage(error: any): string {
  return error instanceof OrderActionError ? error.code : error?.error?.Message || error?.error?.message || 'status_update_failed';
}

function normalizeOrder(order: any): any {
  if (!order || typeof order !== 'object') return order;
  const result = { ...order };
  if ('Status' in order) result.Status = orderStatusCode(order.Status);
  if ('OrderStatus' in order) result.OrderStatus = orderStatusCode(order.OrderStatus);
  if ('PaymentStatus' in order) result.PaymentStatus = paymentStatusCode(order.PaymentStatus);
  if ('StatusLogs' in order) result.StatusLogs = (order.StatusLogs || []).map(normalizeOrder);
  return result;
}

export function normalizeOrderResponse<T>(response: T): T {
  const envelope = response as any;
  if (!envelope || typeof envelope !== 'object') return response;
  if (envelope.Value) return { ...envelope, Value: normalizeOrderResponse(envelope.Value) };
  if (!envelope.Data) return response;
  const data = envelope.Data;
  return { ...envelope, Data: data.Orders ? {
    ...data, Orders: { ...data.Orders, Result: (data.Orders.Result || []).map(normalizeOrder) }
  } : normalizeOrder(data) };
}
